<template>
  <VCard
    :outlined="$vuetify.breakpoint.smAndDown"
    :raised="isActive"
    :class="{ 
      act: isSelected, 
      'current-avatar': isCurrentAvatar 
    }"
    class="interviewer-item item"
    @click="select"
  >
    <VResponsive
      :aspect-ratio="16 / 9"
      max-width="100%"
      class="relative interviewer-item__responsive"
      :class="{ 'interviewer-item__responsive--big' : interviewersLength <= 4 }"
    >
      <video
        ref="video"
        volume="50"
        :src="videoSrc"
        :poster="posterSrc"
        playsinline
        preload="metadata"
      />
      <button
        class="video-btn"
        v-if="!playing"
        @mousedown.stop
        @touchstart.stop
        @click.stop="play"
      >
        {{ $t('watch') }}
        <img src="../../assets/img/interview/play.svg" alt="" />
      </button>
      <button v-else @click.stop="pause" class="video-btn">
        {{ $t('stop') }}
        <img src="../../assets/img/interview/pause.svg" alt="" />
      </button>
    </VResponsive>
    <div class="interviewer-item__text-wrap">
      <div class="interviewer-item__type">
        <img :src="require(`@/assets/img/interviewer/${interviewerTypeImg}.svg`)" />
        <span>
          {{ interviewerType }}
        </span>
      </div>
      <div class="interviewer-item__name">
        {{ interviewerName }}
      </div>
      <div class="interviewer-item__counts-questions">
        <template v-if="countsQuestions">
          {{ $t('canAsk') }}: {{ countsQuestions }}
        </template>
        <template v-else>
          <div>
            {{ $t('voicingAnyQ') }}
          </div>
          <div>
            {{ $t('maxQuestios') }}: {{ limitedPresetQuestions }}
          </div>
        </template>
      </div>
    </div>
  </VCard>
</template>

<script>
import {
  toRefs, ref, onMounted, onBeforeUnmount, watch, computed,
} from '@vue/composition-api';
import useVideo from '@/compositions/useVideo';
import useInterviewerMedia from '@/compositions/useInterviewerMedia';
import useGlobalEventEmitter from '@/compositions/useGlobalEventEmitter';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'VacancyPresetInterviewersItem',
  props: {
    interviewer: {
      type: Object,
      required: true,
    },
    closed: {
      type: Boolean,
      default: false,
    },
    selectedInterviewer: {
      type: Object,
      required: null,
      default: null,
    },
    limitedPresetQuestions: {
      type: Number,
      required: true,
    },
    interviewersLength: {
      type: Number,
      required: true,
    },
    currentAvatar: {
      type: String,
      default: null,
      required: false,
    },
  },
  setup(props, { emit }) {
    const videoSrc = ref(null);
    const posterSrc = ref(null);
    const isSelected = ref(false);
    
    const isActive = computed(() => {
      return isSelected.value || isCurrentAvatar.value;
    });
    const {
      interviewer, closed, selectedInterviewer, currentAvatar,
    } = toRefs(props);
    const { locale, t } = useI18n();

    const interviewerName = computed(() => {
      const { name } = interviewer.value;
      if (typeof name === 'object' && name !== null) {
        return locale.value === 'ru' ? name.ru : name.en;
      }
      return name;
    });

    const interviewerType = computed(() => (interviewer.value.actor_id ? t('aiAvatar') : t('dictor')));
    const interviewerTypeImg = computed(() => (interviewer.value.actor_id ? 'aiAvatar' : 'dictor'));

    const countsQuestions = computed(() => interviewer.value?.counts?.questions);
    
    const isCurrentAvatar = computed(() => {
      if (isSelected.value) {
        return true;
      }
      if (selectedInterviewer.value !== null) {
        return selectedInterviewer.value?.actor_id === interviewer.value?.actor_id;
      }
      return currentAvatar.value && interviewer.value?.actor_id === currentAvatar.value;
    });
    const {
      video, play, pause, preload, playing,
    } = useVideo({ emit });
    const { subscribe, emitEvent } = useGlobalEventEmitter('interviewers-video');
    let unsubscribePlay = null;
    let unsubscribePause = null;

    unsubscribePlay = subscribe('play', (id) => {
      if (interviewer.value?.id !== id) {
        pause();
      }
    });

    unsubscribePause = subscribe('pause', () => {
      pause();
    });

    subscribe('select', (selectedItem) => {
      const selectedActorId = selectedItem?.actor_id || selectedItem?.id;
      const currentActorId = interviewer.value?.actor_id || interviewer.value?.id;
      
      if (selectedItem && selectedActorId !== currentActorId) {
        isSelected.value = false;
      }
      if (selectedItem && selectedActorId === currentActorId) {
        isSelected.value = true;
      }
      if (!selectedItem) {
        isSelected.value = false;
      }
    });

    async function loadVideo() {
      const { getVideoSrc } = useInterviewerMedia();
      if (interviewer.value.preview_url) {
        videoSrc.value = interviewer.value.preview_url;
        posterSrc.value = interviewer.value.image_url;
      } else {
        videoSrc.value = await getVideoSrc(interviewer.value);
        const parts = interviewer.value.videoUrl.split('/');
        const matchedInterviewerName = parts[parts.length - 1];
        posterSrc.value = require(`@/assets/img/interviewer/${matchedInterviewerName}.jpg`);
      }
    }

    watch(closed, (newValue) => {
      if (!newValue) {
        isSelected.value = false;
        emit('selected', null);
        emitEvent('select', null);
      }
    });

    onMounted(() => {
      preload();
      loadVideo();
    });

    onBeforeUnmount(() => {
      if (typeof unsubscribePlay === 'function') {
        unsubscribePlay();
      }
      if (typeof unsubscribePause === 'function') {
        unsubscribePause();
      }
      pause();
    });

    return {
      video,
      play() {
        play();
        emitEvent('play', interviewer.value?.id);
      },
      select() {
        if (!isSelected.value) {
          isSelected.value = true;
          emit('selected', interviewer.value);
          emitEvent('select', interviewer.value);
        }
      },
      pause,
      playing,
      videoSrc,
      posterSrc,
      isSelected,
      isActive,
      isCurrentAvatar,
      countsQuestions,
      interviewerName,
      interviewerType,
      interviewerTypeImg,
    };
  },
};
</script>

<style lang="scss" scoped>
.interviewer-item {
  border-radius: 4px;
  min-height: 100%;
  &.item {
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.16);
  }
  &.act {
    border: 2px solid #1fc57f;
    border-radius: 2px;
    position: relative;
    &:after {
      content: url('../../assets/img/interview/check.svg');
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      background: #1fc57f;
      color: #fff;
      text-align: center;
      position: absolute;
      right: -0.75rem;
      top: -0.75rem;
    }
  }
  
  &.current-avatar {
    border: 2px solid #1FC57F;
    border-radius: 2px;
    position: relative;
  }
  &__responsive {
    @media screen and (min-width: 599px) {
      max-height: 125px;
    }
    max-height: 155px;
    &--big {
      max-height: 155px;
    }
    video {
      width: 100%;
    }
  }
  &__text-wrap {
    padding: 1.25rem;
  }
  &__name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #212121;
  }
  &__counts-questions {
    font-size: 0.75rem;
    color: #212121;
  }
  &__type {
    font-size: 0.75rem;
    color: #1FC57F;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    img {
      margin-right: 0.5rem;
    }
  }
}
.video-btn {
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 500;
  position: absolute;
  bottom: 0.3rem;
  right: 5px;
  padding: 3px 5px;
  box-shadow: none;
  background: #1FC57F;
  border-radius: 4px;
  img {
    border-radius: 50%;
    height: 17px;
    width: 17px;
    margin: 0 0 0 5px;
  }
  &:focus {
    outline: none;
  }
}
</style>

<i18n locale="en" lang="yaml">
watch: Watch the demo
stop: Stop the demo
canAsk: Can ask questions
from: from
ai: AI
dictor: Dictor
aiAvatar: AI Avatar
voicingAnyQ: Voicing any questions
maxQuestios: Maximum number of questions
</i18n>

<i18n locale="ru" lang="yaml">
watch: Посмотреть демо
stop: Остановить демо
canAsk: Озвучено вопросов
from: из
ai: ИИ
dictor: Диктор
aiAvatar: ИИ Аватар
voicingAnyQ: Озвучка любых вопросов
maxQuestios: Максимальное кол-во вопросов
</i18n>
